import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Printer, ScanLine, UserPlus, Search, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import BarcodeScanner from '../shared/BarcodeScanner';
import apiService from '../../services/api';
import { errorMessage, unwrap } from '../../utils/apiResponse';
import fieldWorkService from '../../services/fieldWork';
import {
  WORKER_TYPE_LABELS,
  workerDisplayName,
  type LabourProvider,
  type Worker,
  type WorkerType,
} from '../../types/fieldWork';
import WorkerFormModal from './WorkerFormModal';
import FormModal, { Field, inputClass } from './FormModal';

interface OrgUser {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

const TYPE_BADGE: Record<WorkerType, string> = {
  employee: 'bg-indigo-50 text-indigo-700',
  casual: 'bg-sky-50 text-sky-700',
  contractor_worker: 'bg-amber-50 text-amber-700',
};

/**
 * The roster: everyone who does work, app account or not. Print picker cards
 * for a selection (or everyone), find a worker by scanning their card, and put
 * existing staff on the roster in one go.
 */
export default function WorkersPanel({ providers }: { providers: LabourProvider[] }) {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<Worker | 'new' | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setWorkers(await fieldWorkService.getWorkers({ include_inactive: showInactive }));
    } catch (e) {
      setLoadError(errorMessage(e, 'Could not load the roster'));
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workers;
    return workers.filter(w =>
      `${w.first_name} ${w.last_name} ${w.preferred_name ?? ''} ${w.picker_code} ${w.labour_provider_name ?? ''}`
        .toLowerCase().includes(q));
  }, [workers, search]);

  const toggle = (id: number) => setSelected(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const printCards = async () => {
    setPrinting(true);
    setPrintError(null);
    try {
      const blob = await fieldWorkService.getPickerCardsPdf([...selected]);
      // Same download pattern as certificates: works in the browser and the app,
      // where opening a blob: URL in a new window does not.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'picker-cards.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setPrintError(errorMessage(e, 'Could not make the picker cards'));
    } finally {
      setPrinting(false);
    }
  };

  const onScan = async (code: string) => {
    setScanning(false);
    try {
      const w = await fieldWorkService.findWorkerByCode(code);
      if (w) {
        setScanMessage(null);
        setEditing(w);
      } else {
        setScanMessage(`No worker has the code ${code.trim().toUpperCase()}.`);
      }
    } catch (e) {
      setScanMessage(errorMessage(e, 'Could not look up that code'));
    }
  };

  const saved = (w: Worker) => {
    setEditing(null);
    setWorkers(list => {
      const i = list.findIndex(x => x.id === w.id);
      if (i === -1) return [...list, w];
      const next = [...list];
      next[i] = w;
      return showInactive || w.active ? next : next.filter(x => x.id !== w.id);
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[12rem]">
          <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            aria-label="Search workers"
            className={`${inputClass} pl-9`}
            placeholder="Search name, code or provider"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" /> Add worker
        </Button>
        <Button size="sm" variant="outline" onClick={() => setImporting(true)}>
          <UserPlus className="h-4 w-4" /> From staff
        </Button>
        <Button size="sm" variant="outline" onClick={() => setScanning(true)}>
          <ScanLine className="h-4 w-4" /> Scan card
        </Button>
        <Button size="sm" variant="outline" onClick={printCards} disabled={printing || workers.length === 0}>
          <Printer className="h-4 w-4" />
          {printing ? 'Preparing…' : selected.size ? `Print ${selected.size} card${selected.size === 1 ? '' : 's'}` : 'Print all cards'}
        </Button>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
        <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
          className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
        <span className="text-neutral-600">Show people who have finished up</span>
      </label>

      {scanMessage && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">{scanMessage}</p>}
      {printError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{printError}</p>}
      {loadError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{loadError}</p>}

      {loading ? (
        <p className="text-sm text-neutral-500 py-6 text-center">Loading roster…</p>
      ) : visible.length === 0 ? (
        <div className="text-center py-10 text-sm text-neutral-500">
          {workers.length === 0
            ? 'Nobody on the roster yet. Add pickers and contractors here — they do not need an app account.'
            : 'Nobody matches that search.'}
        </div>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Workers">
          {visible.map(w => (
            <li key={w.id} className="flex items-start gap-3 px-3 py-3">
              <input
                type="checkbox"
                aria-label={`Select ${workerDisplayName(w)}`}
                checked={selected.has(w.id)}
                onChange={() => toggle(w.id)}
                className="mt-1 rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200"
              />
              <button type="button" className="flex-1 min-w-0 text-left" onClick={() => setEditing(w)}>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium text-neutral-900 truncate">{workerDisplayName(w)}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_BADGE[w.worker_type]}`}>
                    {WORKER_TYPE_LABELS[w.worker_type]}
                  </span>
                  {w.user_id && <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">App user</span>}
                  {!w.active && <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600">Finished</span>}
                </div>
                <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap gap-x-3">
                  <span className="font-mono text-neutral-700">{w.picker_code}</span>
                  {w.labour_provider_name && <span>{w.labour_provider_name}</span>}
                  {w.crews && w.crews.length > 0 && <span>{w.crews.map(c => c.crew_name).join(', ')}</span>}
                </div>
              </button>
              {/* Pickers without a login are inducted here too — on a supervisor's phone. */}
              <Link
                to={`/inductions/people/w${w.id}`}
                className="shrink-0 rounded p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-yellow-700"
                aria-label={`Inductions for ${workerDisplayName(w)}`}
                title="Inductions"
              >
                <ShieldCheck className="h-4 w-4" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <WorkerFormModal
          worker={editing === 'new' ? null : editing}
          providers={providers}
          onClose={() => setEditing(null)}
          onSaved={saved}
        />
      )}

      {scanning && (
        <BarcodeScanner label="Scan picker card" onScan={onScan} onClose={() => setScanning(false)} />
      )}

      {importing && (
        <StaffImportModal
          existingUserIds={new Set(workers.filter(w => w.user_id).map(w => w.user_id as number))}
          onClose={() => setImporting(false)}
          onDone={() => { setImporting(false); load(); }}
        />
      )}
    </div>
  );
}

function StaffImportModal({ existingUserIds, onClose, onDone }: {
  existingUserIds: Set<number>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [type, setType] = useState<WorkerType>('employee');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService.get<{ users?: OrgUser[] } | OrgUser[]>('/users')
      .then(res => {
        const d = unwrap(res);
        const list = Array.isArray(d) ? d : d?.users ?? [];
        setUsers(list.filter(u => !existingUserIds.has(u.id)));
      })
      .catch(e => setError(errorMessage(e, 'Could not load staff')));
    // existingUserIds is a snapshot for this dialog's lifetime
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await fieldWorkService.addWorkersFromUsers([...picked], type);
      onDone();
    } catch (e) {
      setError(errorMessage(e, 'Could not add them to the roster'));
    } finally {
      setSaving(false);
    }
  };

  const name = (u: OrgUser) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User #${u.id}`;

  return (
    <FormModal
      title="Add staff to the roster"
      onClose={onClose}
      onSubmit={submit}
      submitLabel={picked.size ? `Add ${picked.size}` : 'Add'}
      submitting={saving}
      submitDisabled={picked.size === 0}
      error={error}
    >
      <Field label="Add them as" htmlFor="imp-type">
        <select id="imp-type" className={inputClass} value={type} onChange={e => setType(e.target.value as WorkerType)}>
          <option value="employee">Employee</option>
          <option value="casual">Casual</option>
        </select>
      </Field>
      {users.length === 0 ? (
        <p className="text-sm text-neutral-500">Everyone in the organisation is already on the roster.</p>
      ) : (
        <ul className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 max-h-72 overflow-y-auto">
          {users.map(u => (
            <li key={u.id}>
              <label className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={picked.has(u.id)}
                  onChange={() => setPicked(s => {
                    const n = new Set(s);
                    if (n.has(u.id)) n.delete(u.id); else n.add(u.id);
                    return n;
                  })}
                  className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200"
                />
                <span className="text-neutral-800">{name(u)}</span>
                {u.email && <span className="text-xs text-neutral-500 truncate">{u.email}</span>}
              </label>
            </li>
          ))}
        </ul>
      )}
    </FormModal>
  );
}
